/**
 * PushStatusBadge Component
 *
 * Displays a persistent badge showing the Shopify push status for an enrichment job.
 * Reads from job.pushResult (written by backend after Shopify confirms).
 *
 * States:
 * - Pushing: Blue spinner badge (status === 'pushing')
 * - Pushed (success): Green check badge
 * - Partial (success with errors): Yellow warning badge
 * - Failed: Red X badge
 *
 * No optimistic updates - only displays confirmed Firestore state.
 */

import React from 'react';
import { Badge, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faExclamationTriangle, faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import './PushStatusBadge.css';

/**
 * Format a timestamp for display
 * @param {Date|string|{seconds: number}|object} timestamp
 * @returns {string}
 */
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';

    let date;
    if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else if (timestamp.seconds) {
        // Firestore Timestamp
        date = new Date(timestamp.seconds * 1000);
    } else if (timestamp.toDate) {
        // Firestore Timestamp with toDate method
        date = timestamp.toDate();
    } else {
        return 'Unknown time';
    }

    // Format as relative time or absolute
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Fall back to formatted date
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
};

/**
 * PushStatusBadge - Shows persistent push status from job.pushResult
 *
 * @param {object} props
 * @param {object} props.job - Enrichment job object with pushResult and status fields
 * @param {boolean} props.compact - If true, renders a smaller badge without label
 */
const PushStatusBadge = ({ job, compact = false }) => {
    // Don't render if no push-related state
    if (!job) return null;

    const { status, pushResult } = job;

    // Show nothing if not pushed and not currently pushing
    if (!pushResult && status !== 'pushing' && status !== 'pushed') {
        return null;
    }

    // State: Currently pushing (in progress)
    if (status === 'pushing') {
        return (
            <Badge bg="info" className={`push-status-badge pushing ${compact ? 'compact' : ''}`}>
                <Spinner animation="border" size="sm" className="push-spinner" />
                {!compact && <span className="push-label">Pushing...</span>}
            </Badge>
        );
    }

    // State: Has pushResult - determine success/partial/failed
    if (pushResult) {
        const hasErrors = pushResult.errors?.length > 0;
        const isSuccess = pushResult.success && !hasErrors;
        const isPartial = pushResult.success && hasErrors;
        const isFailed = !pushResult.success;

        // Build tooltip content
        let tooltipContent;
        if (isSuccess) {
            tooltipContent = `Pushed to Shopify ${formatTimestamp(pushResult.pushedAt)}`;
        } else if (isPartial) {
            const errorPreview = pushResult.errors.slice(0, 2).join('; ');
            tooltipContent = `Partial push: ${errorPreview}${pushResult.errors.length > 2 ? '...' : ''}`;
        } else {
            const errorPreview = pushResult.errors?.slice(0, 2).join('; ') || 'Push failed';
            tooltipContent = `Failed: ${errorPreview}`;
        }

        const tooltip = <Tooltip id={`push-tooltip-${job.id}`}>{tooltipContent}</Tooltip>;

        if (isSuccess) {
            return (
                <OverlayTrigger placement="top" overlay={tooltip}>
                    <Badge bg="success" className={`push-status-badge success ${compact ? 'compact' : ''}`}>
                        <FontAwesomeIcon icon={faCheck} className="push-icon" />
                        {!compact && <span className="push-label">Pushed</span>}
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (isPartial) {
            return (
                <OverlayTrigger placement="top" overlay={tooltip}>
                    <Badge bg="warning" className={`push-status-badge partial ${compact ? 'compact' : ''}`}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="push-icon" />
                        {!compact && <span className="push-label">Partial</span>}
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (isFailed) {
            return (
                <OverlayTrigger placement="top" overlay={tooltip}>
                    <Badge bg="danger" className={`push-status-badge failed ${compact ? 'compact' : ''}`}>
                        <FontAwesomeIcon icon={faTimes} className="push-icon" />
                        {!compact && <span className="push-label">Failed</span>}
                    </Badge>
                </OverlayTrigger>
            );
        }
    }

    // Status is 'pushed' but no pushResult yet (edge case during sync)
    if (status === 'pushed') {
        return (
            <Badge bg="success" className={`push-status-badge success ${compact ? 'compact' : ''}`}>
                <FontAwesomeIcon icon={faCloudUploadAlt} className="push-icon" />
                {!compact && <span className="push-label">Pushed</span>}
            </Badge>
        );
    }

    return null;
};

export default PushStatusBadge;
